import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "../lib/api";

interface TranscriptRow {
  id: string;
  chatId: number;
  chatTitle: string | null;
  messageId: number;
  fromName: string | null;
  fromUsername: string | null;
  mediaType: string | null;
  transcriptionStatus: string | null;
  transcriptionText: string | null;
  transcriptionError: string | null;
  transcriptionDurationSec: number | null;
  transcriptionCompletedAt: string | null;
  sentAt: string;
}

interface BotRow {
  id: string;
  name: string;
  userEmail: string | null;
}

function formatDuration(sec: number | null): string {
  if (!sec) return "—";
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function statusColor(status: string | null): string {
  switch (status) {
    case "completed":
      return "text-green-600";
    case "processing":
      return "text-blue-600";
    case "pending":
      return "text-yellow-600";
    case "failed":
      return "text-red-600";
    case "skipped":
      return "text-gray-500";
    default:
      return "text-gray-400";
  }
}

export default function ClientTranscriptsPage() {
  const [statusFilter, setStatusFilter] = useState<string>("completed");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedBotId, setSelectedBotId] = useState<string>("");
  const [customTitle, setCustomTitle] = useState<string>("");

  const { data: listData, isLoading } = useQuery<{ rows: TranscriptRow[] }>({
    queryKey: [`/api/admin/client-transcripts?limit=100&status=${statusFilter}`],
  });

  const { data: botsData } = useQuery<{ bots: BotRow[] }>({
    queryKey: ["/api/admin/bots-for-transcripts"],
  });

  const selected = listData?.rows.find((r) => r.id === selectedId) ?? null;

  const attachMutation = useMutation({
    mutationFn: async (payload: { transcriptId: string; botId: string; title: string }) => {
      const res = await apiRequest(
        "POST",
        `/api/admin/client-transcripts/${payload.transcriptId}/attach-to-bot`,
        { botId: payload.botId, title: payload.title },
      );
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/client-transcripts"] });
      alert("Добавлено в knowledge base бота");
      setCustomTitle("");
    },
    onError: (err: Error) => alert(`Ошибка: ${err.message}`),
  });

  return (
    <div className="p-6">
      <h1 className="text-2xl font-semibold mb-4">Транскрипты из клиентских чатов</h1>

      <div className="flex gap-2 mb-4">
        {["completed", "processing", "pending", "failed", "skipped"].map((s) => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={`px-3 py-1 rounded text-sm ${
              statusFilter === s ? "bg-primary text-primary-foreground" : "bg-muted"
            }`}
          >
            {s}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted">
              <tr>
                <th className="text-left p-2">Дата</th>
                <th className="text-left p-2">Клиент</th>
                <th className="text-left p-2">Автор</th>
                <th className="text-left p-2">Длит.</th>
                <th className="text-left p-2">Статус</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={5} className="p-4 text-center text-muted-foreground">
                    Загрузка...
                  </td>
                </tr>
              )}
              {listData?.rows.map((r) => (
                <tr
                  key={r.id}
                  onClick={() => setSelectedId(r.id)}
                  className={`cursor-pointer hover:bg-muted/50 ${
                    selectedId === r.id ? "bg-muted" : ""
                  }`}
                >
                  <td className="p-2 whitespace-nowrap">
                    {new Date(r.sentAt).toLocaleDateString("ru-RU")}
                  </td>
                  <td className="p-2">{r.chatTitle ?? r.chatId}</td>
                  <td className="p-2">{r.fromName ?? "—"}</td>
                  <td className="p-2">{formatDuration(r.transcriptionDurationSec)}</td>
                  <td className={`p-2 ${statusColor(r.transcriptionStatus)}`}>
                    {r.transcriptionStatus}
                  </td>
                </tr>
              ))}
              {listData?.rows.length === 0 && (
                <tr>
                  <td colSpan={5} className="p-4 text-center text-muted-foreground">
                    Нет транскриптов со статусом «{statusFilter}»
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="border rounded-lg p-4">
          {!selected ? (
            <p className="text-muted-foreground">Выберите транскрипт слева</p>
          ) : (
            <div>
              <h3 className="font-semibold mb-2">
                {selected.chatTitle ?? "чат"} · {selected.fromName ?? "—"}
              </h3>
              <p className="text-xs text-muted-foreground mb-3">
                {new Date(selected.sentAt).toLocaleString("ru-RU")} · длительность{" "}
                {formatDuration(selected.transcriptionDurationSec)} ·{" "}
                {selected.mediaType}
              </p>
              {selected.transcriptionError && (
                <p className="text-sm text-red-600 mb-3">
                  Ошибка: {selected.transcriptionError}
                </p>
              )}
              {selected.transcriptionText && (
                <div className="mb-4">
                  <textarea
                    className="w-full h-64 p-2 border rounded text-sm font-mono"
                    value={selected.transcriptionText}
                    readOnly
                  />
                </div>
              )}

              {selected.transcriptionStatus === "completed" && (
                <div className="border-t pt-3">
                  <h4 className="font-medium text-sm mb-2">
                    Добавить в knowledge base бота
                  </h4>
                  <select
                    value={selectedBotId}
                    onChange={(e) => setSelectedBotId(e.target.value)}
                    className="w-full mb-2 p-2 border rounded text-sm"
                  >
                    <option value="">— выбрать бота —</option>
                    {botsData?.bots.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.name} · {b.userEmail ?? "—"}
                      </option>
                    ))}
                  </select>
                  <input
                    type="text"
                    placeholder="Заголовок документа (опционально)"
                    value={customTitle}
                    onChange={(e) => setCustomTitle(e.target.value)}
                    className="w-full mb-2 p-2 border rounded text-sm"
                  />
                  <button
                    disabled={!selectedBotId || attachMutation.isPending}
                    onClick={() =>
                      attachMutation.mutate({
                        transcriptId: selected.id,
                        botId: selectedBotId,
                        title: customTitle,
                      })
                    }
                    className="w-full p-2 bg-primary text-primary-foreground rounded disabled:opacity-50 text-sm"
                  >
                    {attachMutation.isPending
                      ? "Добавляю..."
                      : "Добавить в knowledge base"}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
